import classNames from 'clsx'
import { bool, func, node, object, oneOf, oneOfType, string } from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import './Collapsible.scss'

import { TENANT_AT, TENANT_BE, TENANT_COM, TENANT_IT } from 'shared/consts'
import noop from 'shared/utils/noop'
import Anchor from 'views/components/atoms/Anchor'
import ButtonCollapsible from 'views/components/molecules/ButtonCollapsible'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withDataQA from 'views/enhancers/withDataQA'

import { HIDE_ON_INIT_ATTR, POSITION_LAST, POSITION_TYPES } from './consts'

const Collapsible = props => {
  const {
    allScreensizes,
    alwaysDisplayHeading,
    anchorId,
    bordered,
    children,
    className,
    disabled,
    expanded,
    forceDisplayWhenCollapsed,
    handleClick,
    headerColor,
    headerProps,
    headingLevel: HeadingTag,
    id,
    inFilter,
    inModal,
    isClientsideControlled,
    label,
    light,
    position,
    positionSmallTiny,
    rounded,
    sectionId,
    displayOverflow,
    propRef,
    ...rest
  } = props
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const contentAttrs = {}
  const contentRef = useRef(null)
  const [cssParams, setCssParams] = useState(() => {
    const initialValue = {
      height: '0px',
    }

    if (!forceDisplayWhenCollapsed && !isClientsideControlled) {
      initialValue.display = 'none'
      contentAttrs[HIDE_ON_INIT_ATTR] = true
    }

    return initialValue
  })

  useEffect(() => {
    if (isClientsideControlled) {
      setCssParams(
        expanded
          ? {
              height: contentRef.current?.offsetHeight || 'auto',
              display: 'block',
            }
          : { height: '0px' }
      )
    }
  }, [expanded, isClientsideControlled])

  const headerClassname = classNames('m-Collapsible__header', {
    'm-Collapsible__header--last': !position || position === POSITION_LAST,
    [`m-Collapsible__header--${positionSmallTiny}@small-tiny`]:
      positionSmallTiny,
  })
  const headingClassname = classNames('m-Collapsible__heading', {
    'm-Collapsible__heading--always-display': alwaysDisplayHeading,
  })
  const buttonClassname = classNames('m-Collapsible__button u-no-margin', {
    'a-h3': ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(HeadingTag),
  })

  const sectionClassName = classNames(
    {
      'm-Collapsible': allScreensizes,
      'm-Collapsible@small-tiny': !allScreensizes && !inFilter,
      'm-Collapsible--bordered': bordered,
      [`m-Collapsible--${position}`]: position,
      'm-Collapsible--light': light,
      [`m-Collapsible--${positionSmallTiny}@small-tiny`]: positionSmallTiny,
      'm-Collapsible--modal': inModal,
      'm-Collapsible--filter': inFilter,
      'disabled-collapsable': disabled,
      'disabled-collapsable-by-tenant':
        disabled &&
        [TENANT_COM, TENANT_IT, TENANT_AT, TENANT_BE].includes(tenant), // Due to WSCR-2868 and WSCR-2922 tasks, we make an experiment to gray out disabled collapsible for now only for COM IT and AT tenants, after experiment will be finished we will provide those changes for BE tenant within further task
      'm-Collapsible--display-overflow': displayOverflow,
    },
    className
  )

  const cssParamsValue = isClientsideControlled || !expanded ? cssParams : null

  return (
    <section
      className={sectionClassName}
      id={sectionId || undefined}
      ref={propRef}
      {...rest}
    >
      {anchorId && <Anchor id={anchorId} expander />}
      <header {...headerProps} className={headerClassname}>
        <HeadingTag className={headingClassname}>{label}</HeadingTag>
        <HeadingTag className={buttonClassname}>
          <ButtonCollapsible
            ariaControls={id}
            disableClientSideHook={isClientsideControlled}
            expanded={expanded}
            headerColor={headerColor}
            id={'button-' + id}
            light={light}
            name="collapsible"
            onClick={handleClick}
            position={position}
            positionSmallTiny={positionSmallTiny}
            rounded={rounded}
          >
            {label}
          </ButtonCollapsible>
        </HeadingTag>
      </header>
      <div
        aria-hidden={expanded ? null : 'true'}
        className="m-Collapsible__body"
        id={id}
        style={cssParamsValue}
        tabIndex={-1}
        {...contentAttrs}
      >
        <div ref={contentRef} className="m-Collapsible__content">
          {children}
        </div>
      </div>
    </section>
  )
}

Collapsible.propTypes = {
  allScreensizes: bool,
  alwaysDisplayHeading: bool,
  anchorId: string,
  bordered: bool,
  children: node,
  className: string,
  disabled: bool,
  expanded: bool,
  forceDisplayWhenCollapsed: bool,
  handleClick: func,
  headerColor: string,
  headerProps: object,
  headingLevel: string,
  id: string.isRequired,
  inFilter: bool,
  inModal: bool,
  isClientsideControlled: bool,
  label: oneOfType([node, string]).isRequired,
  light: bool,
  position: oneOf(POSITION_TYPES),
  positionSmallTiny: oneOf(POSITION_TYPES),
  rounded: bool,
  sectionId: string,
  displayOverflow: bool,
  propRef: oneOfType([func, object]),
}

Collapsible.defaultProps = {
  allScreensizes: false,
  alwaysDisplayHeading: false,
  anchorId: null,
  bordered: null,
  children: <div />,
  disabled: false,
  expanded: false,
  forceDisplayWhenCollapsed: false,
  handleClick: noop,
  headerColor: null,
  headingLevel: 'h3',
  inFilter: false,
  inModal: false,
  isClientsideControlled: false,
  light: null,
  position: null,
  positionSmallTiny: null,
  rounded: true,
  sectionId: null,
}

export default withDataQA(Collapsible)
